/**
 * Briques communes du modèle (architecture, 4.2).
 *
 * Tout document porte un identifiant Mongo, un horodatage et ses auteurs.
 * Les textes affichés au public sont bilingues, les prix sont des entiers en
 * centimes, les dimensions sont en centimètres et gardent leur unité d'origine.
 */
import { z } from 'zod';
/** Un ObjectId Mongo, représenté par sa forme hexadécimale. */
export const idSchema = z
    .string()
    .regex(/^[0-9a-f]{24}$/, 'Identifiant attendu : 24 caractères hexadécimaux.');
/** Les deux langues du produit. Le français d'abord (D15). */
export const LANGUES = ['fr', 'en'];
export const langueSchema = z.enum(LANGUES);
/**
 * Texte bilingue. Le français est obligatoire ; le site retombe dessus quand
 * l'anglais manque.
 */
export const bilingueSchema = z.object({
    fr: z.string(),
    en: z.string().optional(),
});
/** Slug stable, jamais recalculé après publication. */
export const slugSchema = z
    .string()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug attendu : minuscules, chiffres et tirets.');
export const emailSchema = z.email();
/** Devises ISO 4217 acceptées sur le marché de l'art. */
export const DEVISES = ['EUR', 'USD', 'GBP', 'CHF'];
export const deviseSchema = z.enum(DEVISES);
/** Un montant : toujours un entier de centimes, jamais un flottant. */
export const centimesSchema = z.int();
/** Un montant positif ou nul. */
export const centimesPositifsSchema = z.int().min(0);
/**
 * Une date saisie : un `Date`, ou sa forme texte telle qu'elle arrive en JSON.
 *
 * Plus strict que `z.coerce.date()`, qui ferait de `null`, `0` ou `true` un
 * 1er janvier 1970 parfaitement valide.
 */
export const dateSaisieSchema = z.union([z.date(), z.string().min(1)]).pipe(z.coerce.date());
/** Un prix : des centimes et une devise. */
export const prixSchema = z.object({
    centimes: centimesSchema,
    devise: deviseSchema,
});
/**
 * Taux exprimés en points de base : 10 000 points de base = 100 %.
 * La TVA à 5,5 % vaut 550, la TVA à 20 % vaut 2 000.
 */
export const POINTS_DE_BASE = 10_000;
export const tauxPointsDeBaseSchema = z.int().min(0).max(POINTS_DE_BASE);
/** Unités dans lesquelles la galerie a mesuré l'œuvre. */
export const UNITES_DIMENSION = ['cm', 'mm', 'in'];
export const uniteDimensionSchema = z.enum(UNITES_DIMENSION);
/**
 * Dimensions d'une œuvre. Toujours stockées en centimètres ; l'unité d'origine
 * est conservée pour réafficher ce que la galerie a saisi.
 */
export const dimensionsSchema = z.object({
    hauteur: z.number().min(0),
    largeur: z.number().min(0),
    profondeur: z.number().min(0).optional(),
    diametre: z.number().min(0).optional(),
    uniteOrigine: uniteDimensionSchema.default('cm'),
});
/** Une adresse postale. */
export const adresseSchema = z.object({
    libelle: z.string().optional(),
    ligne1: z.string(),
    ligne2: z.string().optional(),
    codePostal: z.string(),
    ville: z.string(),
    pays: z.string().length(2, 'Code pays ISO 3166-1 alpha-2 attendu.'),
});
/** Horodatage et auteurs, présents sur tous les documents (4.2). */
export const champsAudit = {
    createdAt: z.date(),
    updatedAt: z.date(),
    createdBy: idSchema,
    updatedBy: idSchema,
};
/** Ajoute `createdAt`, `updatedAt`, `createdBy` et `updatedBy` à un schéma. */
export function withAudit(schema) {
    return schema.extend(champsAudit);
}
/**
 * Champs posés par la base, jamais par l'appelant : masque à retirer pour
 * obtenir le schéma d'entrée d'API.
 */
export const CHAMPS_DOCUMENT = {
    _id: true,
    createdAt: true,
    updatedAt: true,
    createdBy: true,
    updatedBy: true,
};
/** Rattachement polymorphe vers un autre document de la galerie. */
export const CIBLES_RATTACHEMENT = [
    'artwork',
    'artist',
    'contact',
    'exhibition',
    'movement',
    'invoice',
    'edition',
    'post',
];
export const cibleRattachementSchema = z.enum(CIBLES_RATTACHEMENT);
export const rattachementSchema = z.object({
    type: cibleRattachementSchema,
    id: idSchema,
});
//# sourceMappingURL=commun.js.map